import { DOCUMENT, DestroyRef, Injectable, InjectionToken, computed, inject, signal } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs';

/** Koliko često otvorena aplikacija pita ima li nove verzije (dohvat `ngsw.json`, ~1 kB). */
export const PWA_PROVJERA_AZURIRANJA_MS = new InjectionToken<number>('PWA_PROVJERA_AZURIRANJA_MS', {
  providedIn: 'root',
  factory: () => 60 * 60 * 1000,
});

/** Ponovno učitavanje stranice (zamjenjivo u testovima; jsdom ga nema). */
export const PWA_PONOVNO_UCITAJ = new InjectionToken<() => void>('PWA_PONOVNO_UCITAJ', {
  providedIn: 'root',
  factory: () => {
    const doc = inject(DOCUMENT);
    return () => doc.location.reload();
  },
});

/**
 * - `nema`: nema ništa novo;
 * - `spremna`: nova verzija je preuzeta u pozadini i čeka osvježavanje;
 * - `neispravna`: verzija u cacheu je neupotrebljiva (preglednik je obrisao dio datoteka),
 *   aplikaciju treba ponovno učitati s mreže.
 */
export type StanjeAzuriranja = 'nema' | 'spremna' | 'neispravna';

/**
 * Ažuriranje aplikacije kroz Angular service worker.
 *
 * Service worker sam preuzme novu verziju u pozadini (pri svakom otvaranju, satnoj provjeri
 * i povratku na karticu). Kad je spremna, prikazuje se nenametljiva obavijest; aktivira se
 * osvježavanjem, nikad sama usred rada (izvođač može biti usred unosa).
 */
@Injectable({ providedIn: 'root' })
export class PwaAzuriranjeService {
  private readonly sw = inject(SwUpdate);
  private readonly doc = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly intervalMs = inject(PWA_PROVJERA_AZURIRANJA_MS);
  private readonly ponovnoUcitaj = inject(PWA_PONOVNO_UCITAJ);

  private readonly _stanje = signal<StanjeAzuriranja>('nema');
  private readonly odgodeno = signal(false);
  private pokrenut = false;

  readonly stanje = this._stanje.asReadonly();
  /** Service worker radi (produkcijski build, preglednik ga podržava); bez njega obavijesti nema. */
  readonly aktivno = this.sw.isEnabled;
  /** Obavijest je vidljiva dok je izvođač ne odgodi; nova verzija ili kvar je vraćaju. */
  readonly obavijestVidljiva = computed(() => this._stanje() !== 'nema' && !this.odgodeno());

  pokreni(): void {
    if (this.pokrenut || !this.sw.isEnabled) {
      return;
    }
    this.pokrenut = true;

    const verzije = this.sw.versionUpdates
      .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
      .subscribe(() => {
        if (this._stanje() !== 'neispravna') {
          this._stanje.set('spremna');
        }
        this.odgodeno.set(false);
      });
    const kvar = this.sw.unrecoverable.subscribe(() => {
      this._stanje.set('neispravna');
      this.odgodeno.set(false);
    });

    const win = this.doc.defaultView;
    const interval = win ? win.setInterval(() => void this.provjeri(), this.intervalMs) : undefined;
    const naVidljivost = (): void => {
      if (this.doc.visibilityState === 'visible') {
        void this.provjeri();
      }
    };
    this.doc.addEventListener('visibilitychange', naVidljivost);

    this.destroyRef.onDestroy(() => {
      verzije.unsubscribe();
      kvar.unsubscribe();
      if (interval !== undefined) {
        win?.clearInterval(interval);
      }
      this.doc.removeEventListener('visibilitychange', naVidljivost);
    });
  }

  /** Pita server za novu verziju. Bez mreže ili prije registracije SW-a tiho odustaje. */
  async provjeri(): Promise<boolean> {
    if (!this.sw.isEnabled) {
      return false;
    }
    try {
      return await this.sw.checkForUpdate();
    } catch {
      return false;
    }
  }

  /** Učitava novu verziju (novo učitavanje stranice dobiva najnoviju verziju iz SW-a). */
  osvjezi(): void {
    this.ponovnoUcitaj();
  }

  /** „Kasnije”: skriva obavijest do sljedeće nove verzije. Kvar se ne može odgoditi. */
  odgodi(): void {
    if (this._stanje() === 'spremna') {
      this.odgodeno.set(true);
    }
  }
}
